import { useState, useEffect, useRef } from 'react'

// Define breakpoints to match Tailwind's default breakpoints
export const breakpoints = {
  md: 584,
} as const

export interface ContainerState {
  // Container dimensions
  width: number
  height: number

  // Semantic size types
  isMobile: boolean // < md
}

/**
 * Hook for responsive behavior based on container dimensions
 *
 * @param containerRef - Reference to the container element to track
 * @returns Object containing container dimensions and breakpoint information
 */
export function useContainerSize<T extends HTMLElement = HTMLDivElement>(
  containerRef: React.RefObject<T>,
): ContainerState {
  // State for container dimensions
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  })

  // Track container dimensions
  useEffect((): (() => void) => {
    if (!containerRef?.current) return () => {}

    const element = containerRef.current

    // Set initial dimensions
    setDimensions({
      width: element.clientWidth,
      height: element.clientHeight,
    })

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.unobserve(element)
      resizeObserver.disconnect()
    }
  }, [containerRef])

  // Compute breakpoint flags
  const { width, height } = dimensions

  // Semantic size types
  const isMobile = width < breakpoints.md

  return {
    // Container dimensions
    width,
    height,

    // Semantic size types
    isMobile,
  }
}

/**
 * Helper hook to create a container reference
 * @returns A ref to attach to your container element
 */
export function useContainerRef<T extends HTMLElement = HTMLDivElement>(): React.RefObject<T> {
  // Using type assertion to fix TypeScript error
  return useRef<T>(null) as React.RefObject<T>
}
